import { FormInstance } from "antd";
import { useCallback } from "react";
import { useAppDispatch } from "../../../../../../../store";
import { useDefaultDeleteModal } from "../../../../../../../lib/hooks/defaultDeleteModal";
import {
  deletePayoffQuote,
  createPayoffQuote,
  editPayoffQuote
} from "../../../../../../../acions/accounts/payoffQuote";
import { IPayoffQuotePost } from "../../../../../../../types/PayoffQute";
import { useFormatData } from "../components/PayoffForm/hooks/useFormatData";
import { TPrintData } from "../types";

type TButtonHandlersProps = {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setId: React.Dispatch<React.SetStateAction<string | null>>;
  reloadData: () => void;
  parentId?: string;
  form?: FormInstance;
  setIsDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
  setItemToPrint: React.Dispatch<React.SetStateAction<TPrintData>>;
};

export const useButtonHandlers = ({
  setIsOpen,
  setId,
  reloadData,
  parentId,
  form,
  setIsDisabled,
  setItemToPrint
}: TButtonHandlersProps) => {
  const dispatch = useAppDispatch();

  const { openDeleteModal } = useDefaultDeleteModal(deletePayoffQuote, reloadData, setIsOpen, () => setId(null));

  const { getFormattedOutcomingData } = useFormatData();

  const onDeleteBtnClick = useCallback(
    (id: string) => {
      openDeleteModal(id, "Delete Payoff Quote");
    },
    [openDeleteModal]
  );

  const onCancelBtnClick = useCallback(() => {
    setId(null);
    setIsOpen(false);
  }, [setId, setIsOpen]);

  const onSuccess = useCallback(
    (isNew = false) => {
      setIsDisabled && setIsDisabled(false);
      setIsOpen(isNew);
      setId(null);
      reloadData();
    },
    [reloadData, setId, setIsDisabled, setIsOpen]
  );

  const create = useCallback(
    async (req: IPayoffQuotePost, isNew: boolean) => {
      try {
        await Promise.resolve(dispatch(createPayoffQuote(req)));

        form?.resetFields();
        onSuccess(isNew);
      } catch {
        setIsDisabled && setIsDisabled(false);
      }
    },
    [dispatch, form, onSuccess, setIsDisabled]
  );

  const edit = useCallback(
    async (id: string, req: IPayoffQuotePost) => {
      try {
        await Promise.resolve(dispatch(editPayoffQuote(req, id)));

        onSuccess();
      } catch {
        setIsDisabled && setIsDisabled(false);
      }
    },
    [dispatch, onSuccess, setIsDisabled]
  );

  const onSaveBtnClick = useCallback(
    async (isNew: boolean, id: string | null) => {
      try {
        const formData = await form?.validateFields();

        setIsDisabled && setIsDisabled(true);

        const req = getFormattedOutcomingData(formData, parentId);

        id ? edit(id, req) : create(req, isNew);
      } catch (error) {
        setIsDisabled && setIsDisabled(false);
      }
    },
    [create, edit, form, getFormattedOutcomingData, parentId, setIsDisabled]
  );

  const onSaveAndPrint = useCallback(
    async (id: string) => {
      try {
        const formData = await form?.validateFields();

        setIsDisabled && setIsDisabled(true);

        const req = getFormattedOutcomingData(formData, parentId);

        await Promise.resolve(dispatch(editPayoffQuote(req, id)));

        setIsDisabled && setIsDisabled(false);

        reloadData();

        setItemToPrint({ id, type: req.receiver.type });
      } catch {
        setIsDisabled && setIsDisabled(false);
      }
    },
    [dispatch, form, getFormattedOutcomingData, parentId, reloadData, setItemToPrint, setIsDisabled]
  );

  return {
    onDeleteBtnClick,
    onCancelBtnClick,
    onSaveBtnClick,
    onSaveAndPrint
  };
};
